import { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSpinner } from '@/fontawesome.ts';
import Modal from '@/components/ui/Modal.tsx';
import NotificationStatsCardCheckbox from './NotificationStatsCardCheckbox';
import RecommendationStatsModal from './RecommendationStatsModal.tsx';
import { APIRoutes } from '@/services/api.routes.ts';
import { handleAPIError, showSuccessToast, showErrorToast } from '@/utils/toasts.ts';
import { toCurrencyCentValue } from '@/utils/helpers.ts';
import type { LongTermRecommendation, RecommendationCreateStats } from '@/models';

interface AddLongTermRecommendationAdjustementModalProps {
  isOpen: boolean;
  onClose: () => void;
  longTermTrade: LongTermRecommendation | null;
  onAdjustmentAdded: (longTermTrade: LongTermRecommendation) => void;
}

export default function AddLongTermRecommendationAdjustementModal({
  isOpen,
  onClose,
  longTermTrade,
  onAdjustmentAdded
}: AddLongTermRecommendationAdjustementModalProps) {
  const [price, setPrice] = useState<number>(0);
  const [sendCommunity, setSendCommunity] = useState(true);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);

  // Stats preview state
  const [estimatedStats, setEstimatedStats] = useState<RecommendationCreateStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [totalUserCount, setTotalUserCount] = useState<number | null>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  // Stats modal state
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [createdStats, setCreatedStats] = useState<RecommendationCreateStats | null>(null);
  const [updatedTrade, setUpdatedTrade] = useState<LongTermRecommendation | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!longTermTrade) return;

    // Validation
    if (price <= 0) {
      showErrorToast('Preis muss größer als 0 sein');
      return;
    }

    setLoading(true);

    try {
      const requestData = {
        price:        toCurrencyCentValue(price),
        sendDiscord:  sendCommunity,
        sendTelegram: sendCommunity,
        sendSCN:      sendCommunity,
        sendPush:     sendCommunity,
        sendMails:    sendCommunity,
        comment:      comment.trim() !== '' ? comment.trim() : null,
      };

      const response = await APIRoutes.addLongTermTradeAdjustement(longTermTrade.id, requestData, false);
      setUpdatedTrade(response.recommendation);
      setCreatedStats(response.stats);
      setShowStatsModal(true);
    } catch (error) {
      handleAPIError(error, 'Fehler beim Hinzufügen des Einstiegs');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setPrice(0);
      setSendCommunity(true);
      setComment('');
      setShowStatsModal(false);
      setCreatedStats(null);
      setUpdatedTrade(null);
      setEstimatedStats(null);
      setStatsError(null);
      onClose();
    }
  };

  const handleStatsModalClose = () => {
    if (updatedTrade !== null) {
      onAdjustmentAdded(updatedTrade);
    }
    showSuccessToast('Einstieg erfolgreich hinzugefügt');
    handleClose();
  };

  // Check if form is valid for fetching stats
  const isFormValid = price > 0 && longTermTrade !== null;

  // Build request payload for both submit and dryRun
  const buildRequestPayload = () => {
    if (longTermTrade === null) return null;

    return {
      price:        toCurrencyCentValue(price),
      sendDiscord:  sendCommunity,
      sendTelegram: sendCommunity,
      sendSCN:      sendCommunity,
      sendPush:     sendCommunity,
      sendMails:    sendCommunity,
      comment:      comment.trim() !== '' ? comment.trim() : null,
    };
  };

  // Fetch estimated notification stats in dryRun mode
  const fetchEstimatedStats = async () => {
    if (!isFormValid || longTermTrade === null) {
      setEstimatedStats(null);
      setStatsError(null);
      setStatsLoading(false);
      return;
    }

    const requestPayload = buildRequestPayload();
    if (requestPayload === null) {
      setEstimatedStats(null);
      setStatsError(null);
      setStatsLoading(false);
      return;
    }

    setStatsLoading(true);
    setStatsError(null);

    try {
      const response = await APIRoutes.addLongTermTradeAdjustement(longTermTrade.id, requestPayload, true);
      setEstimatedStats(response.stats);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Fehler beim Laden der Statistiken';
      setStatsError(errorMessage);
      setEstimatedStats(null);
    } finally {
      setStatsLoading(false);
    }
  };

  // Debounced effect to fetch stats when form changes
  useEffect(() => {
    if (!isFormValid) {
      setEstimatedStats(null);
      setStatsError(null);
      return;
    }

    setStatsLoading(true);

    if (debounceRef.current !== null) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      fetchEstimatedStats();
    }, 500);

    return () => {
      if (debounceRef.current !== null) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [price, sendCommunity, comment, longTermTrade?.id]);

  // Fetch total user count when modal opens
  useEffect(() => {
    if (isOpen) {
      APIRoutes.listUsers({ limit: 1 }).then(response => {
        setTotalUserCount(response.total);
      }).catch(() => {
        setTotalUserCount(null);
      });
    }
  }, [isOpen]);

  // Fetch current symbol price when modal opens
  useEffect(() => {
    if (isOpen && longTermTrade !== null && price === 0) {
      APIRoutes.listSymbols({ ticker: longTermTrade.symbolTicker, limit: 1 })
        .then(response => {
          const exactMatch = response.symbols.find(s => s.ticker === longTermTrade.symbolTicker);
          if (exactMatch?.currentData?.price !== null && exactMatch?.currentData?.price !== undefined) {
            setPrice(exactMatch.currentData.price);
          }
        })
        .catch(() => {
          // Silently ignore errors - user can still enter price manually
        });
    }
  }, [isOpen, longTermTrade?.symbolTicker]);

  if (!longTermTrade) return null;

  return (
    <>
    <Modal
      isOpen={isOpen && !showStatsModal}
      onClose={handleClose}
      title="Einstieg hinzufügen"
      icon={<FontAwesomeIcon icon={faPlus} className="w-5 h-5 text-blue-600" />}
      maxWidth="2xl"
      closeDisabled={loading}
    >
      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {/* Price */}
        <div>
          <label htmlFor="price" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Preis  *
          </label>
          <input
            type="number"
            id="price"
            value={price || ''}
            onChange={(e) => setPrice(parseFloat(e.target.value) || 0)}
            step="0.01"
            min="0"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder="100.00"
            disabled={loading}
            autoFocus
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Der Preis zum Zeitpunkt des Einstiegs
          </p>
        </div>

        {/* Community Notification */}
        <NotificationStatsCardCheckbox
          checked={sendCommunity}
          onChange={setSendCommunity}
          disabled={loading}
          estimatedStats={estimatedStats}
          statsLoading={statsLoading}
          statsError={statsError}
          totalUserCount={totalUserCount}
        />

        {/* Comment */}
        {sendCommunity && (
          <div>
            <label htmlFor="comment" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Kommentar
            </label>
            <textarea
              id="comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              placeholder="Optional: Zusätzlicher Kommentar für die Benachrichtigung"
              disabled={loading}
            />
          </div>
        )}

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={loading || price <= 0}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                Hinzufügen...
              </>
            ) : (
              'Einstieg hinzufügen'
            )}
          </button>
        </div>
      </form>
    </Modal>

    {createdStats !== null && (
      <RecommendationStatsModal
        isOpen={showStatsModal}
        onClose={handleStatsModalClose}
        stats={createdStats}
        recommendationType="longterm"
        text={`Einstieg erfolgreich hinzugefügt`}
      />
    )}
    </>
  );
}
